import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import ModalWrapper from "./ModalWrapper";
import StandardButton from "../Buttons/StandardButton";

export default function TextInputModal({
  open,
  title,
  description,
  label,
  defaultValue = "",
  confirmLabel,
  cancelLabel,
  testIdPrefix,
  onCancel,
  onSave,
}: {
  open: boolean;
  title: string;
  description?: string;
  label: string;
  defaultValue?: string;
  confirmLabel: string;
  cancelLabel?: string;
  testIdPrefix: string;
  onCancel: () => void;
  onSave: (value: string) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(defaultValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultValue);
      setSaving(false);
    }
  }, [open, defaultValue]);

  if (!open) return null;

  const trimmed = name.trim();

  const handleSave = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <ModalWrapper
      clickOff={saving ? undefined : onCancel}
      bgOpacity="40"
      zIndexClass="z-[80]"
    >
      <div
        className="relative z-[60] mx-4 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl"
        data-testid={`${testIdPrefix}-modal`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-snow-primary">{title}</h3>
        {description ? (
          <p className="mt-1 text-sm text-slate-600">{description}</p>
        ) : null}
        <label className="mt-4 block text-sm font-medium text-slate-700">
          {label}
          <input
            type="text"
            data-testid={`${testIdPrefix}-input`}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={name}
            autoFocus
            disabled={saving}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && trimmed && !saving) {
                e.preventDefault();
                void handleSave();
              }
              if (e.key === "Escape" && !saving) {
                e.preventDefault();
                onCancel();
              }
            }}
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            data-testid={`${testIdPrefix}-cancel`}
            disabled={saving}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onCancel}
          >
            {cancelLabel ?? t("common.cancel")}
          </button>
          <StandardButton
            color="blue"
            label={confirmLabel}
            disabled={!trimmed || saving}
            loading={saving}
            onClick={() => void handleSave()}
            testId={`${testIdPrefix}-save`}
            className="!flex !h-10 !w-24 !items-center !justify-center !px-3 !py-2"
          />
        </div>
      </div>
    </ModalWrapper>,
    document.body,
  );
}
